/**
 * QR Code Encryption/Decryption Utilities
 * 
 * This module provides secure encryption and decryption for QR code data
 * to replace the plain JSON format with encrypted strings.
 * 
 * Format: The encrypted QR code will be a base64-encoded string that contains
 * the encrypted JSON data with worship_id and current_datetime.
 */

import CryptoJS from 'crypto-js';

// Secret key for encryption/decryption
// In production, this should be stored in environment variables
const ENCRYPTION_KEY = process.env.REACT_APP_QR_ENCRYPTION_KEY || 'ccbc-cms-qr-secret-key-2026';

// Initialization vector for AES encryption
const ENCRYPTION_IV = process.env.REACT_APP_QR_ENCRYPTION_IV || 'ccbc-cms-qr-iv';

const isMinorWorshipId = (worshipId) => {
  if (worshipId === null || worshipId === undefined) return false;
  if (typeof worshipId === 'number') return worshipId >= 0 && worshipId <= 9999;
  if (typeof worshipId === 'string' && /^\d+$/.test(worshipId)) return worshipId.length <= 4;
  return false;
};

const validateWorshipMobileRule = (qrData) => {
  const worshipId = qrData?.worship_id;
  const mobile = qrData?.mobile;
  const hasMobile = typeof mobile === 'string' && mobile.trim().length > 0;

  if ((worshipId === null || worshipId === undefined) && !hasMobile) {
    throw new Error('Either worship_id or mobile must be provided');
  }
  if (!isMinorWorshipId(worshipId) && !hasMobile) {
    throw new Error('mobile is required for normal member QR data');
  }
};

/**
 * Get properly sized encryption key (32 bytes for AES-256) - matches Python backend
 */
const getEncryptionKey = () => {
  let key = ENCRYPTION_KEY;
  if (key.length < 32) {
    // Pad the key to 32 bytes with '0'
    key = key + '0'.repeat(32 - key.length);
  } else if (key.length > 32) {
    // Truncate to 32 bytes
    key = key.substring(0, 32);
  }
  return CryptoJS.enc.Utf8.parse(key);
};

/**
 * Get properly sized IV (16 bytes for AES) - matches Python backend
 */
const getEncryptionIV = () => {
  let iv = ENCRYPTION_IV;
  if (iv.length < 16) {
    // Pad the IV to 16 bytes with '0'
    iv = iv + '0'.repeat(16 - iv.length);
  } else if (iv.length > 16) {
    // Truncate to 16 bytes
    iv = iv.substring(0, 16);
  }
  return CryptoJS.enc.Utf8.parse(iv);
};

/**
 * Encrypts QR code data (worship_id and current_datetime) into a secure string
 * @param {Object} qrData - Object containing worship_id and current_datetime
 * @param {number} qrData.worship_id - The worship ID
 * @param {string} qrData.current_datetime - The datetime string
 * @returns {string} Encrypted base64 string
 */
export const encryptQRData = (qrData) => {
  try {
    // Validate input data
    if (!qrData || typeof qrData !== 'object') {
      throw new Error('Invalid QR data: must be an object');
    }

    // Treat blank mobile as not provided so minor member QR can omit mobile.
    const normalizedQrData = {
      ...qrData,
      mobile: (typeof qrData.mobile === 'string' && qrData.mobile.trim() === '') ? null : qrData.mobile
    };

    // worship_id is optional; if provided must be number or string
    if (normalizedQrData.hasOwnProperty('worship_id') && normalizedQrData.worship_id !== null && typeof normalizedQrData.worship_id !== 'number' && typeof normalizedQrData.worship_id !== 'string') {
      throw new Error('Invalid worship_id: must be a number or string if provided');
    }

    if (normalizedQrData.worship_id !== null && normalizedQrData.worship_id !== undefined) {
      normalizedQrData.worship_id = String(normalizedQrData.worship_id).trim().toUpperCase();
      if (!normalizedQrData.worship_id) {
        normalizedQrData.worship_id = null;
      }
    }

    // mobile is optional; if provided must be a non-empty string
    if (normalizedQrData.hasOwnProperty('mobile') && normalizedQrData.mobile !== null && (typeof normalizedQrData.mobile !== 'string' || !normalizedQrData.mobile.trim())) {
      throw new Error('Invalid mobile: must be a non-empty string if provided');
    }

    validateWorshipMobileRule(normalizedQrData);

    if (!normalizedQrData.current_datetime || typeof normalizedQrData.current_datetime !== 'string') {
      throw new Error('Invalid current_datetime: must be a string');
    }

    // Convert data to JSON string
    const jsonString = JSON.stringify(normalizedQrData);
    
    // Get properly sized key and IV (matching Python backend)
    const key = getEncryptionKey();
    const iv = getEncryptionIV();
    
    // Encrypt using AES with explicit key and IV
    const encrypted = CryptoJS.AES.encrypt(jsonString, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    // Return base64 encoded encrypted string
    return encrypted.toString();
  } catch (error) {
    console.error('QR Encryption Error:', error);
    throw new Error(`Failed to encrypt QR data: ${error.message}`);
  }
};

/**
 * Decrypts an encrypted QR code string back to the original data
 * @param {string} encryptedData - Base64 encrypted string from QR code
 * @returns {Object} Decrypted object with worship_id and current_datetime
 */
export const decryptQRData = (encryptedData) => {
  try {
    // Get properly sized key and IV (matching Python backend)
    const key = getEncryptionKey();
    const iv = getEncryptionIV();
    
    // Decrypt the data
    const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    // Convert to string and parse JSON
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    const qrData = JSON.parse(decryptedString);
    validateWorshipMobileRule(qrData);
    return qrData;
  } catch (error) {
    console.error('Error decrypting QR data:', error);
    throw new Error('Failed to decrypt QR data');
  }
};

/**
 * Checks if a QR code string is in the new encrypted format or old JSON format
 * @param {string} qrCode - The QR code string to check
 * @returns {boolean} True if encrypted format, false if JSON format
 */
export const isEncryptedQRCode = (qrCode) => {
  if (!qrCode || typeof qrCode !== 'string') {
    return false;
  }

  // Check if it's JSON format (starts with { and ends with })
  const trimmed = qrCode.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    return false; // JSON format
  }

  // If it's not JSON and is a base64-like string, assume it's encrypted
  // Base64 pattern check (letters, numbers, +, /, = for padding)
  const base64Pattern = /^[A-Za-z0-9+/]+=*$/;
  return base64Pattern.test(trimmed);
};

/**
 * Universal QR code parser that handles both encrypted and legacy JSON formats
 * @param {string} qrCode - The QR code string (either encrypted or JSON)
 * @returns {Object} Parsed QR data with worship_id and current_datetime
 */
export const parseQRCode = (qrCode) => {
  try {
    if (isEncryptedQRCode(qrCode)) {
      // New encrypted format
      console.log('🔐 Parsing encrypted QR code');
      return decryptQRData(qrCode);
    } else {
      // Legacy JSON format
      console.log('📄 Parsing legacy JSON QR code');
      const qrData = JSON.parse(qrCode);
      validateWorshipMobileRule(qrData);
      return qrData;
    }
  } catch (error) {
    console.error('QR Parse Error:', error);
    throw new Error(`Invalid QR code format: ${error.message}`);
  }
};

/**
 * Generates a test encrypted QR code for debugging
 * @param {number} worshipId - The worship ID to use
 * @param {string} datetime - Optional datetime, defaults to current time
 * @returns {string} Encrypted QR code string
 */
export const generateTestEncryptedQR = (worshipId = 123456, datetime = null) => {
  const currentDatetime = datetime || new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 12);
  const worshipIdStr = String(worshipId).trim();
  const isMinor = /^\d{1,4}$/.test(worshipIdStr);
  
  const qrData = {
    worship_id: worshipIdStr,
    mobile: isMinor ? null : '1234567890',
    current_datetime: currentDatetime
  };

  return encryptQRData(qrData);
};

// Export constants for use in other files
export const QR_ENCRYPTION_CONSTANTS = {
  KEY: ENCRYPTION_KEY,
  IV: ENCRYPTION_IV
};